import axiosInstance from '../utils/axios';

/**
 * 세션 관리 서비스
 * 
 * 통합 세션 관리 API 호출을 담당합니다.
 */
const sessionManagementService = {
  /**
   * 통합 세션 목록 조회
   * @param {Object} params - 조회 파라미터
   * @param {string} params.type - 세션 유형 (user|admin|medical_bypass)
   * @param {string} params.status - 상태 (active|expired|terminated)
   * @param {string} params.user_id - 사용자 ID 검색
   * @param {string} params.ip_address - IP 주소 검색
   * @param {number} params.page - 페이지 번호
   * @param {number} params.limit - 페이지당 항목 수
   * @param {string} params.sort - 정렬 기준
   * @param {string} params.order - 정렬 순서 (asc|desc)
   * @returns {Promise<Object>} 세션 목록 및 페이징 정보
   */
  async getSessionList(params = {}) {
    const response = await axiosInstance.get('/sessions', { params });
    return response.data;
  },

  /**
   * 세션 상세 조회
   * @param {string} sessionId - 세션 ID
   * @param {string} sessionType - 세션 유형 (user|medical_bypass)
   * @returns {Promise<Object>} 세션 상세 정보
   */
  async getSessionDetail(sessionId, sessionType = 'user') {
    const response = await axiosInstance.get(`/sessions/${sessionId}`, {
      params: { type: sessionType }
    });
    return response.data;
  },

  /**
   * 세션 통계 조회
   * @returns {Promise<Object>} 세션 통계 데이터
   */
  async getStatistics() {
    const response = await axiosInstance.get('/sessions/statistics');
    return response.data;
  },

  /**
   * 세션 강제 종료
   * @param {string} sessionId - 세션 ID
   * @param {string} sessionType - 세션 유형 (user|medical_bypass)
   * @param {string} reason - 종료 사유
   * @returns {Promise<Object>} 종료 결과
   */
  async terminateSession(sessionId, sessionType = 'user', reason = '') {
    // 의료진 우회 세션은 전용 엔드포인트 사용
    if (sessionType === "medical_bypass") {
      const response = await axiosInstance.post(
        `/security/bypass/sessions/${sessionId}/force-end`,
        {
          reason,
        }
      );
      return response.data;
    }

    // 일반 세션 종료
    const response = await axiosInstance.post(
      `/sessions/${sessionId}/terminate`,
      {
        session_type: sessionType,
        reason,
      }
    );
    return response.data;
  },

  /**
   * 사용자의 모든 세션 강제 종료
   * @param {number} userIdx - 사용자 idx
   * @param {string} reason - 종료 사유
   * @returns {Promise<Object>} 종료 결과
   */
  async terminateAllUserSessions(userIdx, reason = '') {
    const response = await axiosInstance.post(`/sessions/user/${userIdx}/terminate-all`, {
      reason
    });
    return response.data;
  },

  /**
   * 세션 정책 조회
   * @returns {Promise<Object>} 세션 정책 설정 값
   */
  async getPolicies() {
    const response = await axiosInstance.get('/sessions/policies');
    return response.data;
  },

  /**
   * 세션 정책 수정
   * @param {Object} policies - 정책 설정 값
   * @returns {Promise<Object>} 수정된 정책 설정 값
   */
  async updatePolicies(policies) {
    const response = await axiosInstance.put('/sessions/policies', policies);
    return response.data;
  }
};

export default sessionManagementService;
